import { useMemo } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncQuery } from "@/ds";
import {
  listCurvasMensalizacao,
  listDistribuicoesColetor,
  listMoedasProjecao,
} from "@/modules/financeiro-obras/financeiro-obras.projecao.gateway";
import type {
  CategoriaProjecao,
  MoedaOpcao,
  OpcaoProjecao,
} from "@/modules/financeiro-obras/financeiro-obras.projecao.types";

export const OPCOES_PROJECAO_PERMISSAO =
  "Você não tem permissão para consultar as opções da projeção.";
export const OPCOES_PROJECAO_ERRO = "Não foi possível carregar as opções da projeção.";

type PorCategoria = Record<CategoriaProjecao, OpcaoProjecao[]>;

export interface OpcoesProjecao {
  moedas: MoedaOpcao[];
  distribuicoes: PorCategoria;
  curvas: PorCategoria;
}

export type OpcoesGravadas = Partial<
  Record<"distribuicoes" | "curvas", Partial<Record<CategoriaProjecao, OpcaoProjecao | null>>>
>;

const comGravada = (lista: OpcaoProjecao[], gravada: OpcaoProjecao | null | undefined) =>
  gravada && !lista.some((o) => o.id === gravada.id) ? [...lista, gravada] : lista;

function acrescentarGravadas(base: OpcoesProjecao, gravadas: OpcoesGravadas | undefined) {
  if (!gravadas) return base;
  const unir = (tipo: "distribuicoes" | "curvas"): PorCategoria => ({
    SERVICOS: comGravada(base[tipo].SERVICOS, gravadas[tipo]?.SERVICOS),
    MATERIAIS: comGravada(base[tipo].MATERIAIS, gravadas[tipo]?.MATERIAIS),
  });
  return { ...base, distribuicoes: unir("distribuicoes"), curvas: unir("curvas") };
}

const SEM_OPCOES: OpcoesProjecao = {
  moedas: [],
  distribuicoes: { SERVICOS: [], MATERIAIS: [] },
  curvas: { SERVICOS: [], MATERIAIS: [] },
};

async function carregar(signal: AbortSignal): Promise<OpcoesProjecao> {
  const [moedas, distServicos, distMateriais, curvaServicos, curvaMateriais] = await Promise.all([
    listMoedasProjecao({ signal }),
    listDistribuicoesColetor("SERVICOS", { signal }),
    listDistribuicoesColetor("MATERIAIS", { signal }),
    listCurvasMensalizacao("SERVICOS", { signal }),
    listCurvasMensalizacao("MATERIAIS", { signal }),
  ]);
  return {
    moedas,
    distribuicoes: { SERVICOS: distServicos, MATERIAIS: distMateriais },
    curvas: { SERVICOS: curvaServicos, MATERIAIS: curvaMateriais },
  };
}

export function useOpcoesProjecao(gravadas?: OpcoesGravadas, enabled = true) {
  const { status, data, errorMessage, reload } = useAsyncQuery({
    fetcher: carregar,
    queryKey: "projecao-opcoes",
    enabled,
    initialData: SEM_OPCOES,
    forbiddenMessage: OPCOES_PROJECAO_PERMISSAO,
    errorMessage: OPCOES_PROJECAO_ERRO,
    resolveErrorMessage: getErrorMessage,
  });

  const opcoes = useMemo(() => acrescentarGravadas(data, gravadas), [data, gravadas]);

  return { status, opcoes, errorMessage, recarregar: reload };
}
